import React, { ReactElement } from 'react';
import Button from '@/ui/components/shared/Button';
import { useTranslations } from 'next-intl';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

export interface MobileAddToCartProps {
  productName: string;
  price?: string;
  addToCart: () => void;
  disabled?: boolean;
  isAvailable: boolean;
  isBundleSelected?: boolean;
  onNotify: () => void;
  isUS: boolean;
  /**
   * Shown instead of "Add to cart", e.g. to unlock a Nuki Club only item. An element, not any
   * node, so a `condition && <X />` (false) cannot hide the button by accident.
   */
  purchaseActions?: ReactElement;
}

export function MobileAddToCart({
  productName,
  price,
  addToCart,
  disabled,
  isAvailable,
  isBundleSelected,
  onNotify,
  isUS,
  purchaseActions,
}: Readonly<MobileAddToCartProps>) {
  const t = useTranslations('ProductChooser');

  return (
    <div className="fixed bottom-0 left-0 w-full bg-white px-3 py-2 shadow-nuki-blured lg:hidden">
      <div className="flex items-center justify-between gap-3">
        {isAvailable ? (
          <div className="mr-4 flex-1 overflow-hidden">
            <p className="truncate text-lg font-bold text-neutral-90">{productName}</p>
            <p className="truncate font-medium text-neutral-90">
              {price}
              {!isUS && (
                <span className="ml-1 text-sm font-normal text-neutral-50">{t('incl VAT')}</span>
              )}
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 text-red">
            <ExclamationTriangleIcon className="h-12 w-12" />
            <p className="font-bold">
              {isBundleSelected
                ? t('The selected bundle is currently not available')
                : t('This product variant is currently not available')}
            </p>
          </div>
        )}

        {isAvailable ? (
          purchaseActions ?? (
            <Button className="whitespace-nowrap" disabled={disabled} type="button" onClick={addToCart}>
              {t('Add to cart')}
            </Button>
          )
        ) : (
          <Button className="whitespace-nowrap" disabled={disabled} type="button" onClick={onNotify}>
            {t('Notify me')}
          </Button>
        )}
      </div>
    </div>
  );
}
